import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import MuiButtonBase from '@material-ui/core/ButtonBase';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

const styles = theme => ({
  root: {
    display: 'inline',
    padding: 0,
    fontSize: 'inherit',
    textDecoration: 'none',
    color: 'inherit',
    backgroundColor: 'transparent',
    fontFamily: theme.typography.fontFamily,
    textAlign: 'left',
    wordWrap: 'break-word',
    wordBreak: 'break-all',
    userSelect: 'text',
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover, &:focus': {
      cursor: 'pointer',
      textDecoration: 'none',
      color: theme.palette.primary[500],
      textShadow: '.4px 0 0 currentColor, -.4px 0 0  currentColor',
    },
  },
  rootSizeSm: {
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5, 12),
    lineHeight: pxToEm(16, 12),
  },
  rootSizeMd: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
  },
  rootSizeLg: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
  },
  rootColorPrimary: {
    color: theme.palette.primary[600],
  },
  rootColorSecondary: {
    color: theme.palette.grey[650],
    '&:hover, &:focus': {
      color: theme.palette.grey[700],
    },
  },
  rootColorAdd: {
    color: theme.palette.add[500],
  },
  rootColorValidation: {
    color: theme.palette.validation[500],
    '&:hover, &:focus': {
      color: theme.palette.validation[600],
    },
  },
  rootUnderline: {
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  rootBold: {
    fontWeight: 'bold',
  },
  rootDisabled: {
    opacity: '0.5',
  },
  rootBreakWord: {
    wordBreak: 'break-word',
  },
  label: {
    position: 'relative',
    display: 'inherit',
    width: 'inherit',
    maxWidth: 'inherit',
    textOverflow: 'inherit',
    overflow: 'inherit',
    verticalAlign: 'baseline',
  },
  labelInlineLabel: {
    display: 'inline',
  },
});

const Link = ({
  className,
  classes,
  children,
  href,
  state,
  download,
  color,
  disabled,
  size,
  onClick,
  onMouseDown,
  underline,
  bold,
  breakWord,
  history,
  staticContext,
  inlineLabel,
  target,
  ...otherProps
}) => (
  <MuiButtonBase
    className={classNames(className, classes.root, {
      [classes.rootColorPrimary]: color === 'primary',
      [classes.rootColorSecondary]: color === 'secondary',
      [classes.rootColorAdd]: color === 'add',
      [classes.rootColorValidation]: color === 'validation',
      [classes.rootDisabled]: disabled,
      [classes.rootSizeSm]: size && size === 'sm',
      [classes.rootSizeMd]: size && size === 'md',
      [classes.rootSizeLg]: size && size === 'lg',
      [classes.rootUnderline]: underline,
      [classes.rootBold]: bold,
      [classes.rootBreakWord]: breakWord,
    })}
    {...download ? { download } : {}}
    {...href && staticContext ? { href } : {}}
    onMouseDown={onMouseDown}
    disableRipple
    disabled={disabled}
    target={target}
    onClick={href && !download && target !== '_blank'
      ? (event) => {
        if (!href || (href.indexOf('mailto') === -1 && href.indexOf('tel') === -1)) {
          event.preventDefault();
          onClick(event);
          if (href !== history.location.pathname) {
            if (state) {
              history.push({
                pathname: href,
                state,
              });
            } else {
              history.push(href);
            }
          }
        }
      }
      : (event) => {
        if (target !== '_blank') {
          event.preventDefault();
        }
        onClick(event);
      }
    }
    {...otherProps}
  >
    <span
      className={classNames(classes.label, {
        [classes.labelInlineLabel]: inlineLabel,
      })}
    >
      {children}
    </span>
  </MuiButtonBase>
);

Link.propTypes = {
  href: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  state: PropTypes.object,
  download: PropTypes.string,
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'add', 'validation']),
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  onMouseDown: PropTypes.func,
  className: PropTypes.string,
  underline: PropTypes.bool,
  bold: PropTypes.bool,
  breakWord: PropTypes.bool,
  staticContext: PropTypes.bool,
  target: PropTypes.oneOf(['_blank', '_self']),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  inlineLabel: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootColorPrimary: PropTypes.string.isRequired,
    rootColorSecondary: PropTypes.string.isRequired,
    rootColorAdd: PropTypes.string.isRequired,
    rootColorValidation: PropTypes.string.isRequired,
    rootSizeSm: PropTypes.string.isRequired,
    rootSizeMd: PropTypes.string.isRequired,
    rootSizeLg: PropTypes.string.isRequired,
    rootDisabled: PropTypes.string.isRequired,
    rootUnderline: PropTypes.string.isRequired,
    rootBold: PropTypes.string.isRequired,
    rootBreakWord: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelInlineLabel: PropTypes.string.isRequired,
  }).isRequired,
};

Link.defaultProps = {
  href: null,
  state: null,
  download: null,
  color: 'inherit',
  disabled: false,
  size: null,
  className: '',
  underline: false,
  bold: false,
  breakWord: false,
  staticContext: true,
  inlineLabel: false,
  target: '_self',
  onClick: () => {},
  onMouseDown: () => {},
};

export default withRouter(
  withStyles(styles)(Link),
);



// WEBPACK FOOTER //
// ./src/components/common/Link.js